'use client';

import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Modal } from '@components/common/Modal/Modal';
import { useTranslation } from 'next-i18next';
import { useHandleSubscriptionModalClose } from '@components/templates/employer/subscription/utils';
import { CloseTalentSubscriptionHubModal } from '@components/templates/talent/subscription/CloseTalentSubscriptionHubModal';
import { bjToast, Button } from '@components/common';
import { useEffect, useState } from 'react';
import { useSwrCloseRequest } from '@hooks/useSwrApi/endpoints/user-subscription/useSwrCloseRequest';
import { IUserSubscriptionList } from '@type/v1-api-types';

/**
 * @description Renders the upgrade subscription modal, with payment options, pricing and if
 * it's the case the 'not enough credits' and payment status possible steps.
 * The modal can be opened anywhere in the app using the **showUpgradeSubscriptionModal** function.
 */
export const CloseTalentSubscriptionModal = NiceModal.create(
  (props: {
    locale: Locale,
    userSubscriptionInfo?: IUserSubscriptionList;
    successCallback: () => void;
  }) => {
    // Destructure props
    const {
      locale,
      userSubscriptionInfo,
      successCallback,
    } = props;

    // Use a hook to manage the modal state
    const modal = useModal();
    const [step, setStep] = useState<number>(1);
    const [reason, setReason] = useState<number | null>(null);
    const [otherReason, setOtherReason] = useState<string | null>();
    const [isSubmittable, setIsSubmittable] = useState<boolean>(true);

    // Close modal
    const closeModal = () => {
      // Reset next step button
      setIsSubmittable(true);

      void modal.hide();

      setStep(1);
    };

    // Translation
    const { t } = useTranslation('common');

    // Cleanup Redux states and re-fetch after closing the modal depending on the flow step
    const { handleSubscriptionModalClose } = useHandleSubscriptionModalClose();

    const handleNextStep = () => {
      setStep(step + 1);
    };

    // SWR: Make close request
    const { error: closeRequestError } = useSwrCloseRequest(
      locale,
      { closeReason: reason, otherCloseReason: otherReason },
      step === 3,
      successCallback,
    );

    // Check for reason ID and show text area if reason is other
    useEffect(() => {
      if (step === 3) {
        void modal.hide();

        setStep(1);

        if (closeRequestError) {
          bjToast.error(closeRequestError.errors[0].message);
        }
      }

      // On the step for asking reason, set the submit button to be disabled unless a reason is provided
      if (step === 2) {
        setIsSubmittable(Boolean((reason !== null && [1, 2, 3, 4].includes(reason)) || (reason === 0 && otherReason && otherReason.length >= 5)));
      }
    }, [closeRequestError, modal, otherReason, reason, step]);

    // Reset state for other reason text area
    useEffect(() => {
      setOtherReason(null);
    }, [reason]);

    const footerComponent = () => (
      <div className="m-4 flex justify-end gap-2">
        <Button
          rounding="full"
          size="sm"
          color="secondary"
          onClick={closeModal}
        >
          {t('premium.talent.closeRequest.keepSubscription')}
        </Button>
        <Button
          disabled={!isSubmittable}
          rounding="full"
          size="sm"
          color="error"
          onClick={handleNextStep}
        >
          {t('premium.talent.closeRequest.continueClosing')}
        </Button>
      </div>
    );

    // Render component
    // *****************************************
    return (
      <Modal
        isOpen={modal.visible}
        onClose={closeModal}
        afterLeave={handleSubscriptionModalClose}
        size="lg"
        header={t('premium.talent.closeSubscription.header')}
        body={(
          <CloseTalentSubscriptionHubModal
            step={step}
            setReason={setReason}
            setOtherReason={setOtherReason}
            userSubscriptionInfo={userSubscriptionInfo}
          />
        )}
        footer={footerComponent()}
      />
    );
  },
);

/**
 * @description Opens the subscription talent premium modal
 *
 * @example
 * // import
 * import { showUpgradeSubscriptionModal } from '@components/templates/employer/subscription/UpgradeSubscriptionModal';
 * // usage
 * <Button onClick={showUpgradeSubscriptionModal}>Show upgrade subscription modal</Button>
 */
export const showCloseTalentSubscriptionModal = (
  locale: Locale,
  userSubscriptionInfo?: IUserSubscriptionList,
  successCallback?: () => void,
) => {
  void NiceModal.show(CloseTalentSubscriptionModal, {
    locale,
    userSubscriptionInfo,
    successCallback,
  });
};
